import dayjs from 'dayjs'

import { ClusterCluster, OrgOrganization } from '@clinic/client'

import { axiosInstance } from './base'

export const NGM_API_BASE_PATH = '/ngm/api/v1'

export interface NgmClusterContext {
  orgExternalId: string
  clusterId: string
  provider: string
  region: string
  deployType: string
  projectId?: string
}

export interface StatementsDatasourceResponse {
  v2_available?: boolean
}

export function buildNgmHeaders(
  ctx: NgmClusterContext
): Record<string, string> {
  return {
    'x-deploy-type': ctx.deployType || 'dedicated',
    'x-provider': ctx.provider,
    'x-region': ctx.region,
    'x-org-id': ctx.orgExternalId,
    'x-cluster-id': ctx.clusterId,
    ...(ctx.projectId ? { 'x-project-id': ctx.projectId } : {})
  }
}

export function ngmContextFromCluster(
  cluster: ClusterCluster,
  orgExternalId: string
): NgmClusterContext {
  return {
    orgExternalId,
    clusterId: cluster.id || '',
    provider: cluster.provider || '',
    region: cluster.region || '',
    deployType: cluster.deployType || 'dedicated',
    projectId: cluster.projectID || ''
  }
}

export function getStatementsDatasource(ctx: NgmClusterContext) {
  return axiosInstance
    .get<StatementsDatasourceResponse>(
      `${NGM_API_BASE_PATH}/statements/datasource`,
      {
        headers: buildNgmHeaders(ctx),
        // A failed capability probe intentionally falls back to Statement V1.
        skipErrorHandler: true
      }
    )
    .then((res) => res.data)
}

type listFilesReq = {
  fileType: string
  beginTime: string
  endTime: string
  cluster: ClusterCluster
  org: OrgOrganization
}

const ONE_DAY_SECONDS = 1 * 24 * 60 * 60
const TWO_DAYS_SECONDS = 2 * ONE_DAY_SECONDS
const SEVEN_DAYS_SECONDS = 7 * ONE_DAY_SECONDS

export async function listClusterFiles({
  fileType,
  beginTime,
  endTime,
  cluster,
  org
}: listFilesReq) {
  // TODO: extract to util method
  let s = 0
  let e = 0
  if (endTime === 'now' && beginTime.startsWith('now')) {
    // currently only support -24h, -2d, -7d
    const deltaStr = beginTime.substring(4)
    let deltaSecs = ONE_DAY_SECONDS
    if (deltaStr === '2d') {
      deltaSecs = TWO_DAYS_SECONDS
    } else if (deltaStr === '7d') {
      deltaSecs = SEVEN_DAYS_SECONDS
    }
    e = dayjs().unix()
    s = e - deltaSecs
  } else {
    s = Number(beginTime)
    e = Number(endTime)
  }

  return axiosInstance.get(
    `${NGM_API_BASE_PATH}/${fileType}/list?start=${s}&end=${e}`,
    {
      // Preserve the existing files API header contract. Statements and Top RU
      // use the richer shared NGM cluster context above.
      headers: {
        'x-provider': cluster.provider ?? '',
        'x-region': cluster.region ?? '',
        'x-org-id': org.externalID ?? '',
        'x-project-id': cluster.projectID ?? '',
        'x-cluster-id': cluster.id ?? ''
      }
    }
  )
}
